const baseUrl = import.meta.env.VITE_CRM_API_BASE_URL ?? "/api/v1";
const REQUEST_TIMEOUT_MS = 8_000;

export class WorkforceApiError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
  ) {
    super(`Workforce request failed (${status})`);
    this.name = "WorkforceApiError";
  }
}

function csrf(): string {
  const value = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("crm_session_csrf="))
    ?.split("=")[1];
  if (!value) throw new WorkforceApiError(401, "SESSION_EXPIRED");
  return decodeURIComponent(value);
}

async function request<T>(
  path: string,
  method: "GET" | "POST" | "PATCH" | "DELETE" = "GET",
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (method !== "GET") {
    headers["Content-Type"] = "application/json";
    headers["x-csrf-token"] = csrf();
    headers["x-idempotency-key"] = `workforce-fe-${crypto.randomUUID()}`;
  }
  const controller = new AbortController();
  const timeout = window.setTimeout(
    () => controller.abort(),
    REQUEST_TIMEOUT_MS,
  );
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      method,
      credentials: "include",
      cache: "no-store",
      headers,
      signal: controller.signal,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    if (controller.signal.aborted)
      throw new WorkforceApiError(0, "REQUEST_TIMEOUT");
    if (cause instanceof WorkforceApiError) throw cause;
    throw new WorkforceApiError(0, "NETWORK_FAILURE");
  } finally {
    window.clearTimeout(timeout);
  }
  if (!response.ok) {
    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      /* category only */
    }
    const code =
      payload &&
      typeof payload === "object" &&
      "code" in payload &&
      typeof payload.code === "string"
        ? payload.code
        : undefined;
    throw new WorkforceApiError(response.status, code);
  }
  return response.json() as Promise<T>;
}

export const workforceApi = {
  myWork: () =>
    request<{ summary: WorkforceSummary; items: WorkforceAssignment[] }>(
      "/work/me",
    ),
  unassigned: () =>
    request<{ items: WorkforceAssignment[] }>("/dispatch/unassigned"),
  teams: () => request<{ items: WorkforceTeam[] }>("/teams"),
  teamMembers: (teamId: string) =>
    request<{ items: WorkforceMembership[] }>(`/teams/${teamId}/members`),
  teamQueue: (teamId: string) =>
    request<{ queues: WorkforceQueue[]; assignments: WorkforceAssignment[] }>(
      `/teams/${teamId}/queue`,
    ),
  teamWorkload: (teamId: string) =>
    request<WorkforceWorkload>(`/teams/${teamId}/workload`),
  warnings: () => request<{ items: WorkforceSla[] }>("/sla/warnings"),
  breaches: () => request<{ items: WorkforceSla[] }>("/sla/breaches"),
  escalations: () => request<{ items: WorkforceEscalation[] }>("/escalations"),
  previewRouting: (body: unknown) =>
    request<WorkforceRoutingPreview>("/dispatch/preview-routing", "POST", body),
  assign: (objectType: string, objectId: string, body: unknown) =>
    request<WorkforceAssignment>(
      `/dispatch/${objectType}/${objectId}/assign`,
      "POST",
      body,
    ),
  accept: (id: string, expectedVersion: number) =>
    request<WorkforceAssignment>(`/assignments/${id}/accept`, "POST", {
      expectedVersion,
      reasonCode: "UI_ACCEPTED",
    }),
  decline: (id: string, expectedVersion: number) =>
    request<WorkforceAssignment>(`/assignments/${id}/decline`, "POST", {
      expectedVersion,
      reasonCode: "UI_DECLINED",
      reasonText: "DEMO UI decline reason",
    }),
  acknowledgeEscalation: (id: string, expectedVersion: number) =>
    request<WorkforceEscalation>(`/escalations/${id}/acknowledge`, "POST", {
      expectedVersion,
      reason: "UI_ACKNOWLEDGED",
    }),
  resolveEscalation: (id: string, expectedVersion: number) =>
    request<WorkforceEscalation>(`/escalations/${id}/resolve`, "POST", {
      expectedVersion,
      reason: "UI_RESOLVED",
    }),
  updateMembership: (id: string, body: unknown) =>
    request<WorkforceMembership>(`/team-memberships/${id}`, "PATCH", body),
};

export type WorkforceSummary = Record<string, number>;
export interface WorkforceAssignment {
  id: string;
  workspaceId: string;
  objectType: string;
  objectId: string;
  teamId: string | null;
  primaryAssigneeId: string | null;
  priority: string;
  status: string;
  acceptanceDueAt: string | null;
  processingDueAt: string | null;
  completedAt: string | null;
  version: number;
  participantRole?: string;
}
export interface WorkforceRoutingPreview {
  decisionId: string;
  matchedRules: string[];
  excludedTeams: string[];
  excludedMembers: string[];
  selectedTeam?: string;
  selectedMember?: string;
  assignmentReason: string;
}
export interface WorkforceTeam {
  id: string;
  code: string;
  name: string;
  teamType: string;
  status: string;
  version: number;
  routingEnabled: boolean;
}
export interface WorkforceMembership {
  id: string;
  userId: string;
  teamId: string;
  membershipRole: string;
  availabilityStatus: string;
  availability?: string;
  capacityWeight: number | string;
  capacity?: number;
  currentLoad?: number;
  activeAssignments?: number;
  delegationUserId?: string | null;
  skills: string[];
  regions: string[];
  languages: string[];
  productCategories: string[];
  sites: string[];
  version: number;
  user?: { displayName: string; status: string };
}
export interface WorkforceQueue {
  id: string;
  code: string;
  name: string;
  objectType: string;
  status: string;
}
export interface WorkforceWorkload {
  teamId: string;
  members: WorkforceMembership[];
}
export interface WorkforceSla {
  id: string;
  assignmentId: string;
  eventType: string;
  status: string;
  dueAt: string;
  warningAt: string;
  version: number;
}
export interface WorkforceEscalation {
  id: string;
  assignmentId: string;
  level: number;
  trigger: string;
  status: string;
  occurredAt: string;
  version: number;
}
